import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import SearchableMultiSelect from '../shared/SearchableMultiSelect';

// Admin-only "Workspace Agents" panel (Admin Dashboard → Workspace Agents tab).
// Lists every workflow in every store and toggles the `new_workspace_workflow`
// tag on each. Tagged workflows run automatically for every newly created
// workspace — bound to that workspace's Google/Meta accounts and website URL —
// and are deliberately never offered in the Agent Library.
//
// Sibling of PublishManagerModal: same embedded-card chrome, same /admin/* API
// surface, same role === 'admin' gate on the server.

const TAG = 'new_workspace_workflow';
const PAGE_SIZE = 25;
// Mirrors the server's POST /admin/workspaces/run-workflows caps.
const RUN_MAX_WORKFLOWS = 10;
const RUN_MAX_WORKSPACES = 20;
// The 200 most recently updated workflows across stores — the same page cap
// GET /admin/workflows/all enforces; enough for a searchable picker.
const RUN_FLOW_FETCH_LIMIT = 200;

const s = {
  embedded: { background: 'var(--surface,#fff)', borderRadius: '14px', width: '100%', maxWidth: '980px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  title: { fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' },
  body: { padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '18px' },
  note: { fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary)', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px', margin: 0 },
  filters: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' },
  search: { flex: '1 1 220px', minWidth: '180px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', outline: 'none' },
  select: { padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', minWidth: '180px' },
  checkLabel: { display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer', whiteSpace: 'nowrap' },
  card: { border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '10px 14px', background: '#fbfcfe', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)', verticalAlign: 'middle' },
  name: { fontWeight: 600 },
  meta: { fontSize: '11px', color: 'var(--text-muted)' },
  tagBtn: (tagged, busy) => ({
    padding: '5px 12px', borderRadius: '8px', fontWeight: 600, fontSize: '12px',
    cursor: busy ? 'progress' : 'pointer', opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
    border: `1px solid ${tagged ? '#fecaca' : 'var(--primary)'}`,
    background: tagged ? '#fff' : 'var(--primary)',
    color: tagged ? '#dc2626' : '#fff',
  }),
  pill: { display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: '99px', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' },
  empty: { fontSize: '13px', color: 'var(--text-muted)', padding: '18px 14px' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', fontSize: '12px', color: 'var(--text-muted)' },
  pageBtn: (disabled) => ({ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: '#fff', color: disabled ? 'var(--text-muted)' : 'var(--text-primary)', fontWeight: 600, fontSize: '12px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }),
  divider: { border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0 0' },
  sectionTitle: { fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: 0 },
  runGrid: { display: 'flex', gap: '14px', flexWrap: 'wrap' },
  runCol: { flex: '1 1 280px', minWidth: '240px', display: 'flex', flexDirection: 'column', gap: '5px' },
  runLabel: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' },
  runFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' },
  runHint: { fontSize: '12px', color: 'var(--text-muted)' },
  runBtn: (disabled) => ({
    padding: '8px 18px', borderRadius: '8px', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap',
    border: '1px solid var(--primary)', background: disabled ? '#e2e8f0' : 'var(--primary)',
    color: disabled ? 'var(--text-muted)' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  failList: { margin: '4px 0 0', paddingLeft: '18px', fontSize: '12px', lineHeight: 1.6, color: '#b91c1c' },
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function WorkspaceAgentsManager() {
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [taggedOnly, setTaggedOnly] = useState(false);
  const [skip, setSkip] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');

  const nameOf = useCallback((key) => stores.find((st) => st.shopKey === key)?.name || key, [stores]);

  useEffect(() => {
    api.get('/admin/stores').then(({ data }) => setStores(data)).catch(() => toast.error('Failed to load stores'));
  }, []);

  // Typing shouldn't fire a cross-store query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change restarts paging — page 3 of the old result set is meaningless.
  useEffect(() => { setSkip(0); }, [debouncedSearch, shopFilter, taggedOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/workflows/all', {
        params: {
          limit: PAGE_SIZE,
          skip,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(shopFilter ? { shop: shopFilter } : {}),
          ...(taggedOnly ? { tag: TAG } : {}),
        },
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load workflows');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [skip, debouncedSearch, shopFilter, taggedOnly]);

  useEffect(() => { load(); }, [load]);

  const toggleTag = async (row) => {
    const tagged = (row.tags || []).includes(TAG);
    setSavingId(row._id);
    try {
      const { data } = await api.post(`/admin/workflows/${row._id}/tags`, {
        tag: TAG,
        action: tagged ? 'remove' : 'add',
      });
      // The "tagged only" view drops a row the moment it loses the tag.
      if (taggedOnly && tagged) {
        setRows((prev) => prev.filter((r) => r._id !== row._id));
        setTotal((prev) => Math.max(0, prev - 1));
      } else {
        setRows((prev) => prev.map((r) => (r._id === row._id ? { ...r, ...data } : r)));
      }
      toast.success(tagged ? `"${row.name}" removed from new-workspace agents` : `"${row.name}" will run for every new workspace`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update tag');
    } finally {
      setSavingId('');
    }
  };

  // —— Run agents for existing workspaces ——
  const [runFlowRows, setRunFlowRows] = useState([]);
  const [runWsOptions, setRunWsOptions] = useState([]);
  const [runWsTruncated, setRunWsTruncated] = useState(false);
  const [runFlowIds, setRunFlowIds] = useState([]);
  const [runWorkspaceIds, setRunWorkspaceIds] = useState([]);
  const [running, setRunning] = useState(false);
  const [runFailures, setRunFailures] = useState([]);

  // Both pickers load once — the flow list is the 200 most recently updated
  // workflows across stores (searchable client-side), the workspace list is
  // every workspace in every account with its account name.
  useEffect(() => {
    api.get('/admin/workflows/all', { params: { limit: RUN_FLOW_FETCH_LIMIT } })
      .then(({ data }) => setRunFlowRows(data.rows || []))
      .catch(() => toast.error('Failed to load flows for the run picker'));
    api.get('/admin/workspaces')
      .then(({ data }) => {
        setRunWsOptions((data.rows || []).map((row) => ({
          value: row.id,
          label: `${row.name || row.id} (${row.accountName})`,
          hint: row.accountShop,
        })));
        setRunWsTruncated(data.truncated === true);
      })
      .catch(() => toast.error('Failed to load workspaces'));
  }, []);

  // Derived so the store-name tooltip fills in once /admin/stores resolves.
  const runFlowOptions = useMemo(
    () => runFlowRows.map((row) => ({ value: row._id, label: row.name, hint: nameOf(row.shop) })),
    [runFlowRows, nameOf],
  );

  const runSelectionValid = runFlowIds.length > 0 && runWorkspaceIds.length > 0
    && runFlowIds.length <= RUN_MAX_WORKFLOWS && runWorkspaceIds.length <= RUN_MAX_WORKSPACES;

  const runForWorkspaces = async () => {
    if (!runSelectionValid || running) return;
    setRunning(true);
    setRunFailures([]);
    const tid = toast.loading('Queueing runs…');
    try {
      const { data } = await api.post('/admin/workspaces/run-workflows', {
        workflowIds: runFlowIds,
        workspaceIds: runWorkspaceIds,
      });
      const results = data.results || [];
      const failed = results.filter((r) => r.error);
      const ok = results.length - failed.length;
      toast.success(`${ok} run${ok === 1 ? '' : 's'} queued${failed.length ? `, ${failed.length} failed` : ''}`, { id: tid });
      if (failed.length) {
        const flowName = (id) => runFlowOptions.find((o) => o.value === id)?.label || id;
        const wsName = (id) => runWsOptions.find((o) => o.value === id)?.label || id;
        setRunFailures(failed.map((f) => `${flowName(f.templateId)} → ${wsName(f.workspaceId)}: ${f.error}`));
      } else {
        setRunFlowIds([]);
        setRunWorkspaceIds([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to queue runs', { id: tid });
    } finally {
      setRunning(false);
    }
  };

  const pageFrom = total === 0 ? 0 : skip + 1;
  const pageTo = Math.min(skip + PAGE_SIZE, total);
  const canPrev = skip > 0;
  const canNext = skip + PAGE_SIZE < total;

  const storeOptions = useMemo(
    () => [...stores].sort((a, b) => a.name.localeCompare(b.name)),
    [stores],
  );

  return (
    <div style={s.embedded}>
      <div style={s.header}>
        <span style={s.title}>Workspace Agents</span>
      </div>

      <div style={s.body}>
        <p style={s.note}>
          Tagged workflows run automatically whenever an account owner creates a workspace. Each one is
          copied into the new workspace and bound to the Google Ads account, Meta ad account and website URL
          captured at creation; a Context node in the workflow writes into that workspace&apos;s
          <strong> Automated Context</strong>. Tagged workflows are never shown in the Workflow Library, and
          their per-workspace copies stay hidden from the store&apos;s My Workflows list.
        </p>

        <div style={s.filters}>
          <input
            style={s.search}
            placeholder="Search workflows by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select style={s.select} value={shopFilter} onChange={(e) => setShopFilter(e.target.value)}>
            <option value="">All stores</option>
            {storeOptions.map((st) => <option key={st.shopKey} value={st.shopKey}>{st.name}</option>)}
          </select>
          <label style={s.checkLabel}>
            <input type="checkbox" checked={taggedOnly} onChange={(e) => setTaggedOnly(e.target.checked)} />
            Tagged only
          </label>
        </div>

        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Workflow</th>
                <th style={s.th}>Store</th>
                <th style={s.th}>Nodes</th>
                <th style={s.th}>Updated</th>
                <th style={{ ...s.th, textAlign: 'right' }}>New-workspace agent</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={s.empty} colSpan={5}>Loading workflows…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td style={s.empty} colSpan={5}>
                    {taggedOnly ? 'No workflows are tagged yet.' : 'No workflows match these filters.'}
                  </td>
                </tr>
              ) : rows.map((row) => {
                const tagged = (row.tags || []).includes(TAG);
                const busy = savingId === row._id;
                return (
                  <tr key={row._id}>
                    <td style={s.td}>
                      <div style={s.name}>{row.name}</div>
                      {tagged && <div style={{ marginTop: '4px' }}><span style={s.pill}>Runs on new workspace</span></div>}
                    </td>
                    <td style={{ ...s.td, ...s.meta }}>{nameOf(row.shop)}</td>
                    <td style={s.td}>{row.nodeCount ?? (row.nodes || []).length}</td>
                    <td style={{ ...s.td, ...s.meta, whiteSpace: 'nowrap' }}>{formatDate(row.updatedAt)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>
                      <button
                        type="button"
                        style={s.tagBtn(tagged, busy)}
                        disabled={busy}
                        onClick={() => toggleTag(row)}
                      >
                        {busy ? 'Saving…' : tagged ? 'Remove tag' : 'Add tag'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={s.pager}>
          <span>{total === 0 ? 'No workflows' : `Showing ${pageFrom}–${pageTo} of ${total}`}</span>
          <span style={{ display: 'flex', gap: '8px' }}>
            <button type="button" style={s.pageBtn(!canPrev)} disabled={!canPrev} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>← Previous</button>
            <button type="button" style={s.pageBtn(!canNext)} disabled={!canNext} onClick={() => setSkip(skip + PAGE_SIZE)}>Next →</button>
          </span>
        </div>

        <hr style={s.divider} />

        <h3 style={s.sectionTitle}>Run agents for existing workspaces</h3>
        <p style={s.note}>
          Queue any flows for workspaces that already exist. Each selected flow is copied into each
          selected workspace — bound to its ad accounts and website like a new-workspace agent — and
          run once; the copies stay hidden, and a Context node writes into that workspace&apos;s
          <strong> Automated Context</strong>. Runs bill the workspace&apos;s account, and
          &ldquo;queued&rdquo; means queued, not finished — check the account&apos;s run history for results.
        </p>

        <div style={s.runGrid}>
          <div style={s.runCol}>
            <span style={s.runLabel}>Flows</span>
            <SearchableMultiSelect
              value={runFlowIds}
              options={runFlowOptions}
              onChange={setRunFlowIds}
              placeholder="— Select flows —"
              summarize={(n) => `${n} flows selected`}
            />
          </div>
          <div style={s.runCol}>
            <span style={s.runLabel}>Workspaces</span>
            <SearchableMultiSelect
              value={runWorkspaceIds}
              options={runWsOptions}
              onChange={setRunWorkspaceIds}
              placeholder="— Select workspaces —"
              summarize={(n) => `${n} workspaces selected`}
            />
            {/* 500 mirrors the server's ADMIN_ALL_WORKSPACES_CAP, like the RUN_MAX constants. */}
            {runWsTruncated && <span style={s.runHint}>Only the first 500 workspaces are listed.</span>}
          </div>
        </div>

        <div style={s.runFoot}>
          <span style={s.runHint}>
            {runFlowIds.length} flow{runFlowIds.length === 1 ? '' : 's'} × {runWorkspaceIds.length} workspace{runWorkspaceIds.length === 1 ? '' : 's'}
            {' '}· max {RUN_MAX_WORKFLOWS} × {RUN_MAX_WORKSPACES} per batch
          </span>
          <button
            type="button"
            style={s.runBtn(!runSelectionValid || running)}
            disabled={!runSelectionValid || running}
            onClick={runForWorkspaces}
          >
            {running ? 'Queueing…' : 'Run for workspaces'}
          </button>
        </div>

        {runFailures.length > 0 && (
          <ul style={s.failList}>
            {runFailures.map((f) => <li key={f}>{f}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
